// Pure utility to decide whether to set the delivery-note toggle programmatically
// and to update internal flags used for detecting user overrides and programmatic acks.
//
// The function is pure: it takes the current "view" of refs/state and returns an
// action (set?: boolean) and the updated flags. This makes it straightforward to
// cover with unit tests.
export interface DeliveryNoteToggleInput {
  prevIsEnabled: boolean | null;
  isEnabled: boolean;
  prevDocumentType: string | null;
  documentType: string | null;
  isEdit: boolean;
  initialDocumentType: string | null;
  isClone: boolean;
  // Doc types where the toggle is shown but should never be auto-recommended to `true` - it
  // stays off until the user explicitly turns it on (see `isDeliveryNoteDisabledByDefault`).
  disabledByDefault: boolean;

  // Formik meta/view
  currentValue: boolean | null | undefined; // current field value (true/false) or undefined for fresh forms
  initialValue: any; // formik initialValue for the field (may be undefined)
  prevValue: boolean | null | undefined; // previously observed value

  // refs/flags
  lastSetByUs: boolean;
  lastProgrammaticValue: boolean | null;
  userOverrode: boolean;
}

export interface DeliveryNoteToggleResult {
  // If set is defined, caller should call setFieldValue(fieldName, set)
  set?: boolean;
  // Updated flags to write back into refs
  lastSetByUs: boolean;
  lastProgrammaticValue: boolean | null;
  userOverrode: boolean;
}

/**
 * Compute whether to programmatically set the toggle and update helper flags.
 */
export function computeDeliveryNoteToggleAction(input: DeliveryNoteToggleInput): DeliveryNoteToggleResult {
  const {
    prevIsEnabled,
    isEnabled,
    prevDocumentType,
    documentType,
    isEdit,
    initialDocumentType,
    isClone,
    disabledByDefault,
    currentValue,
    initialValue,
    prevValue,
    lastSetByUs,
    lastProgrammaticValue,
    userOverrode,
  } = input;

  let nextLastSetByUs = lastSetByUs;
  let nextLastProgrammaticValue = lastProgrammaticValue;
  let nextUserOverrode = userOverrode;

  const docTypeChanged = prevDocumentType !== documentType;

  // If doc type changed, clear past user override so recommendations can apply again.
  if (docTypeChanged) {
    nextUserOverrode = false;
  }

  // Acknowledge programmatic set: if we previously marked "lastSetByUs" and the current
  // value matches the programmatic value we expected, then the programmatic write has been observed.
  if (nextLastSetByUs && nextLastProgrammaticValue !== null && currentValue === nextLastProgrammaticValue) {
    nextLastSetByUs = false;
    nextLastProgrammaticValue = null;
  }

  // Detect a genuine user change (value changed since last observed AND it wasn't our programmatic set)
  const prevValDefined = prevValue !== undefined && prevValue !== null;
  if (prevValDefined && currentValue !== prevValue) {
    if (!nextLastSetByUs) {
      // user changed it
      nextUserOverrode = true;
    } else {
      // this change was caused by our programmatic set; just clear the marker (ack handled above too)
      nextLastSetByUs = false;
      nextLastProgrammaticValue = null;
    }
  }

  // Consider whether the field is "user-edited" relative to its initial value
  const userEdited = initialValue !== undefined && currentValue !== initialValue;

  // Decide action
  // Default: do nothing
  let setAction: boolean | undefined = undefined;

  if (!isEnabled) {
    // If not enabled, force false (programmatic). Clear user override because semantics changed.
    if (currentValue !== false) {
      setAction = false;
      nextLastSetByUs = true;
      nextLastProgrammaticValue = false;
      nextUserOverrode = false;
    }
  } else {
    // isEnabled === true
    const docTypeUnchangedInEdit = isEdit && documentType === initialDocumentType;
    const becameEnabled = prevIsEnabled === false && isEnabled === true;
    const switchedEnabledType = prevIsEnabled === true && isEnabled === true && docTypeChanged;

    if (!docTypeUnchangedInEdit) {
      if (nextUserOverrode) {
        // Respect the user's explicit choice until doc type changes.
        setAction = undefined;
      } else if (disabledByDefault) {
        // Force false for these doc types (e.g. 5.1/5.2), not just "don't auto-enable" -
        // `NewSaleScreen.tsx` seeds a brand-new doc's raw initial value to `true` regardless of
        // doc type, and switching in from an auto-enable doc type (e.g. 1.1 with the toggle on)
        // carries that `true` over too. Without an active force-false here, both cases would leave
        // the toggle on. Stays off until the user explicitly turns it on (handled like any other
        // doc type via `nextUserOverrode` above, once that happens).
        if (currentValue !== false) {
          setAction = false;
          nextLastSetByUs = true;
          nextLastProgrammaticValue = false;
        }
      } else if (becameEnabled || switchedEnabledType || isClone) {
        // Recommend enabling on transition disabled->enabled, enabled->enabled switch, or clone.
        if (currentValue !== true) {
          setAction = true;
          nextLastSetByUs = true;
          nextLastProgrammaticValue = true;
        }
      } else {
        // Non-transition: only auto-enable when the field hasn't been edited by user
        // and has no explicit value (fresh form).
        if (!userEdited && (currentValue === undefined || currentValue === null)) {
          setAction = true;
          nextLastSetByUs = true;
          nextLastProgrammaticValue = true;
        }
      }
    } else {
      // docType unchanged in edit: do nothing
      setAction = undefined;
    }
  }

  return {
    set: setAction,
    lastSetByUs: nextLastSetByUs,
    lastProgrammaticValue: nextLastProgrammaticValue,
    userOverrode: nextUserOverrode,
  };
}
